// Payer & Ruleset Setup Wizard Types

import type { PayerType, RulesetStatus, SubmissionLane } from '@/lib/rules/rulesetTypes';
import type { PlanLaneConfig, LaneType } from './laneTypes';

export interface PayerWizardProgress {
  id: string;
  current_step: number;
  step1_complete: boolean;
  step2_complete: boolean;
  step3_complete: boolean;
  step4_complete: boolean;
  step5_complete: boolean;
  
  // Step 1 - Payer Setup
  payer_name: string;
  payer_type: PayerType;
  jurisdiction: string;
  portal_url: string | null;
  phone: string | null;
  fax: string | null;
  
  // Step 2 - Plan Setup
  plans: PlanConfig[];
  
  // Step 3 - Rule Review
  rules_reviewed: boolean;
  
  // Step 4 - Lane Configuration (per-plan)
  plan_lane_configs: PlanLaneConfig[];
  
  // Created entities
  created_payer_id: string | null;
  created_plan_ids: string[];
  created_ruleset_ids: string[];
  
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface PlanConfig {
  plan_name: string;
  is_default: boolean;
  ruleset_source: RulesetSource;
  source_id: string | null; // ID if cloning from existing
}

export type RulesetSource = 
  | 'COMMERCIAL_BASE_TEMPLATE_V1'
  | 'BWC_OH_BASE_2026'
  | 'EXISTING_PLAN';

// Re-export lane types for convenience
export type { PlanLaneConfig, LaneType } from './laneTypes';
export { createDefaultPlanLaneConfig, validateLaneConfig, recalculateLanePriority, LANE_DISPLAY } from './laneTypes';

export interface RuleReviewSummary {
  auth_requirement_default: string;
  doc_requirements: string[];
  sla_hours: number;
  follow_up_cadence_hours: number;
  rule_count: number;
}

export interface PayerActivationSnapshot {
  id: string;
  payer_id: string;
  activated_at: string;
  activated_by: string;
  payer_config: {
    name: string;
    type: PayerType;
    jurisdiction: string;
    portal_url: string | null;
  };
  plans: Array<{
    id: string;
    name: string;
    is_default: boolean;
    ruleset_id: string;
    ruleset_name: string;
    ruleset_status: RulesetStatus;
    lane_config: PlanLaneConfig;
  }>;
  created_at: string;
}

// Wizard step validation
export interface StepValidation {
  isValid: boolean;
  errors: string[];
}

// Display constants
export const PAYER_TYPES: { value: PayerType; label: string }[] = [
  { value: 'BWC', label: 'Workers\' Comp (BWC)' },
  { value: 'COMMERCIAL', label: 'Commercial' },
  { value: 'MEDICARE', label: 'Medicare' },
  { value: 'OTHER', label: 'Other' },
];

export const SUBMISSION_LANES: { value: LaneType; label: string }[] = [
  { value: 'FHIR_PAS', label: 'FHIR PAS (Electronic)' },
  { value: 'PORTAL', label: 'Payer Portal' },
  { value: 'FAX_PHONE', label: 'Fax/Phone' },
];

export const RULESET_SOURCES: { value: RulesetSource; label: string; description: string }[] = [
  { 
    value: 'COMMERCIAL_BASE_TEMPLATE_V1', 
    label: 'Commercial Base Template V1',
    description: 'Standard commercial payer ruleset with common auth requirements',
  },
  { 
    value: 'BWC_OH_BASE_2026', 
    label: 'Ohio BWC 2026 Base',
    description: 'Ohio Bureau of Workers\' Compensation rules for 2026',
  },
  { 
    value: 'EXISTING_PLAN', 
    label: 'Clone from Existing Plan',
    description: 'Copy rules from an existing payer/plan configuration',
  },
];

export const US_STATES = [
  { value: 'OH', label: 'Ohio' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'MI', label: 'Michigan' },
  { value: 'IN', label: 'Indiana' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'NY', label: 'New York' },
  { value: 'CA', label: 'California' },
  { value: 'TX', label: 'Texas' },
  { value: 'FL', label: 'Florida' },
] as const;

// Initial wizard state
export function createInitialWizardState(): Omit<PayerWizardProgress, 'id' | 'created_at' | 'updated_at' | 'created_by'> {
  return {
    current_step: 1,
    step1_complete: false,
    step2_complete: false,
    step3_complete: false,
    step4_complete: false,
    step5_complete: false,
    
    payer_name: '',
    payer_type: 'COMMERCIAL',
    jurisdiction: 'OH',
    portal_url: null,
    phone: null,
    fax: null,
    
    plans: [],
    
    rules_reviewed: false,
    
    plan_lane_configs: [],
    
    created_payer_id: null,
    created_plan_ids: [],
    created_ruleset_ids: [],
  };
}
